/**
 * Which control each setting gets on the admin Settings page.
 *
 * The server sends a key, a value and little else, so a page driven by the value alone renders
 * a 0..1 matching weight, a byte count and a time zone as three identical textboxes. This table
 * is the missing half: the domain of a setting that its JSON type cannot carry -- the bounds a
 * port really has, the unit a byte ceiling is comfortable to type in, the eight values a header
 * conventionally takes.
 *
 * A flat record rather than a discriminated union per kind. The optional numbers are meaningless
 * on the kinds that ignore them, and a union would buy nothing here: no call site branches on
 * the kind to reach a field that only that kind has -- each row variant already knows which of
 * `min`, `step`, `suffix` and `unit` it reads, and would read them off a narrowed type by the
 * same names. All a union would add is eleven interfaces and a cast at every lookup.
 */

import type { Setting } from "../api/types";

export type FieldKind =
  // resolved from the FIELDS table
  | "slider" // 0..1 ratio, saved on release
  | "number" // spinner, `suffix` printed inside the input
  | "bytes" // typed in `unit`, stored as whole bytes
  | "tags" // pills; a comma-joined string, or a real JSON array
  | "timezone"
  | "autocomplete" // suggestions offered, never enforced
  | "textarea"
  | "path" // monospace single line
  | "url"
  | "epoch" // unix seconds, shown as a local timestamp, not typed
  | "artwork" // an artwork id, with a thumbnail of it
  // resolved from the value's own shape, never listed in FIELDS
  | "options" // `setting.options` is non-empty
  | "switch" // the value is a boolean
  | "checkbox" // boolean rendered as a checkbox control
  | "text"; // anything else

export interface Field {
  kind: FieldKind;
  min?: number;
  max?: number;
  step?: number;
  /** Printed inside the input, leading space included: " s", " min", " kbps". */
  suffix?: string;
  /** `bytes` only: the unit the box is typed in. The stored value is always bytes. */
  unit?: "MiB" | "GiB";
  /** `tags` and `autocomplete`: offered in the dropdown, never enforced. */
  suggestions?: readonly string[];
}

/**
 * The two response headers below are `autocomplete` rather than `options`, on purpose: the
 * server validates neither against a list, so a `Select` would forbid values it would have
 * taken -- a policy this build has not heard of, or a CSP-era spelling. Suggesting the standard
 * spellings and still accepting a typed one is the honest control.
 */
const REFERRER_POLICIES: readonly string[] = [
  "no-referrer",
  "no-referrer-when-downgrade",
  "origin",
  "origin-when-cross-origin",
  "same-origin",
  "strict-origin",
  "strict-origin-when-cross-origin",
  "unsafe-url",
];

const OPENER_POLICIES: readonly string[] = [
  "unsafe-none",
  "same-origin-allow-popups",
  "same-origin",
];

/**
 * Every key whose control is not obvious from its value. Anything absent falls through the
 * `fieldFor` ladder, which is correct for it.
 *
 * No read-only key appears here -- the whole of `Network access` and six of the seven
 * `Services` keys are `config.ENV_ONLY` -- because the row dispatcher renders those as static
 * text before it ever asks for a field, so bounds for them would be config nothing reads.
 */
export const FIELDS: Record<string, Field> = {
  // Ratios. A slider because the value is a weight between two ends, not a quantity anyone
  // types, and it commits on release so a drag is one write.
  show_key_similarity: { kind: "slider", min: 0, max: 1, step: 0.05 },
  match_threshold: { kind: "slider", min: 0, max: 1, step: 0.05 },
  match_time_weight: { kind: "slider", min: 0, max: 1, step: 0.05 },
  match_text_weight: { kind: "slider", min: 0, max: 1, step: 0.05 },
  match_min_text: { kind: "slider", min: 0, max: 1, step: 0.05 },
  match_min_time: { kind: "slider", min: 0, max: 1, step: 0.05 },
  split_min_coverage: { kind: "slider", min: 0, max: 1, step: 0.05 },
  divergence_threshold: { kind: "slider", min: 0, max: 1, step: 0.01 },

  // Spinners. `step` is the increment that makes sense for the setting, not 1 for everything:
  // nudging a poll interval by a second is noise. A `max` appears only where the domain has
  // one -- a port -- and is never invented, which would refuse a value the server accepts.
  show_key_min_prefix: { kind: "number", min: 1, step: 1, suffix: " chars" },
  show_merge_gap_seconds: { kind: "number", min: 0, step: 30, suffix: " s" },
  min_show_seconds: { kind: "number", min: 0, step: 30, suffix: " s" },
  min_track_seconds: { kind: "number", min: 0, step: 5, suffix: " s" },
  show_end_silence_seconds: { kind: "number", min: 0, step: 0.5, suffix: " s" },
  show_end_search_minutes: { kind: "number", min: 0, step: 1, suffix: " min" },
  match_window_minutes: { kind: "number", min: 0, step: 15, suffix: " min" },
  fingerprint_index_batch: { kind: "number", min: 1, step: 10 },
  fingerprint_min_score: { kind: "number", min: 0, step: 1 },
  fingerprint_min_seconds: { kind: "number", min: 0, step: 10, suffix: " s" },
  fingerprint_rescan_growth: { kind: "number", min: 0, step: 50 },
  preferred_bitrate: { kind: "number", min: 32, step: 32, suffix: " kbps" },
  stats_poll_seconds: { kind: "number", min: 1, step: 10, suffix: " s" },
  events_poll_seconds: { kind: "number", min: 60, step: 600, suffix: " s" },
  events_take: { kind: "number", min: 1, step: 10 },
  instance_index: { kind: "number", min: 0, step: 1 },
  recorder_instances: { kind: "number", min: 1, step: 1 },
  divergence_samples: { kind: "number", min: 1, step: 1 },
  edge_retention_hours: { kind: "number", min: 1, step: 1, suffix: " h" },
  edge_relay_max_clients: { kind: "number", min: 0, step: 1 },
  buffer_retention_days: { kind: "number", min: 1, step: 1, suffix: " d" },
  auto_approve_margin_hours: { kind: "number", min: 0, step: 1, suffix: " h" },
  quarantine_hours: { kind: "number", min: 0, step: 1, suffix: " h" },
  chat_replay_offset_seconds: { kind: "number", min: 0, step: 1, suffix: " s" },
  chat_max_streams: { kind: "number", min: 1, step: 16 },
  irc_port: { kind: "number", min: 1, max: 65535 },
  smtp_port: { kind: "number", min: 1, max: 65535 },
  metrics_port: { kind: "number", min: 0, max: 65535 },
  session_days: { kind: "number", min: 1, step: 1, suffix: " d" },
  auth_row_retention_days: { kind: "number", min: 0, step: 1, suffix: " d" },
  email_token_hours: { kind: "number", min: 1, step: 1, suffix: " h" },
  hsts_max_age: { kind: "number", min: 0, step: 86400, suffix: " s" },
  login_max_failures: { kind: "number", min: 1, step: 1 },
  login_address_max_failures: { kind: "number", min: 1, step: 5 },
  login_window_seconds: { kind: "number", min: 1, step: 60, suffix: " s" },
  login_lockout_seconds: { kind: "number", min: 0, step: 60, suffix: " s" },
  login_captcha_after_failures: { kind: "number", min: 0, step: 1 },
  argon2_time_cost: { kind: "number", min: 1, step: 1 },
  argon2_memory_cost: { kind: "number", min: 8, step: 1024, suffix: " KiB" },
  argon2_parallelism: { kind: "number", min: 1, step: 1 },
  settings_refresh_seconds: { kind: "number", min: 0, step: 5, suffix: " s" },
  telegram_api_id: { kind: "number", min: 0, step: 1 },

  // Byte ceilings. Stored in bytes because that is what the code comparing against them holds;
  // typed in the unit a person would say out loud, so a free-space floor is "4.5 GiB" rather
  // than eleven digits nobody can read back.
  min_free_bytes: { kind: "bytes", unit: "GiB", min: 0, step: 1 },
  max_upload_bytes: { kind: "bytes", unit: "MiB", min: 1, step: 1 },
  vk_max_audio_bytes: { kind: "bytes", unit: "MiB", min: 1, step: 10 },

  // Lists and pickers.
  instance_priority: { kind: "tags" },
  station_timezone: { kind: "timezone" },
  referrer_policy: { kind: "autocomplete", suggestions: REFERRER_POLICIES },
  cross_origin_opener_policy: { kind: "autocomplete", suggestions: OPENER_POLICIES },
  content_security_policy: { kind: "textarea" },

  // Filesystem paths: monospace, and no spellcheck squiggle under every directory name.
  buffer_dir: { kind: "path" },
  archive_dir: { kind: "path" },
  fingerprint_work_dir: { kind: "path" },
  edge_db_path: { kind: "path" },

  // Addresses. `type="url"` is what gets a keyboard without a spacebar on a phone.
  station_base_url: { kind: "url" },
  stats_url: { kind: "url" },
  events_url: { kind: "url" },
  playlist_url_template: { kind: "url" },
  fingerprint_url: { kind: "url" },
  edge_link_url: { kind: "url" },
  mail_worker_url: { kind: "url" },
  cap_internal_url: { kind: "url" },
  redis_url: { kind: "url" },
  public_base_url: { kind: "url" },

  // One-offs. Neither is typed: an expiry is the token's own, and a cover is picked elsewhere
  // and only confirmed here, which is why it comes with a thumbnail of what the id points at.
  vk_token_expires: { kind: "epoch" },
  default_cover_artwork_id: { kind: "artwork" },
  // News controls: rendered as checkboxes rather than switches per user requirement.
  news_enabled: { kind: "checkbox" },
  news_auto_changelog: { kind: "checkbox" },
};

/**
 * The control for one setting: the table first, then the value's own shape.
 *
 * A read-only setting never reaches here -- the row dispatcher checks `editable` first and
 * renders the static value cell -- so every branch below is about how a value gets edited.
 */
export function fieldFor(setting: Setting): Field {
  const declared = FIELDS[setting.key];
  if (declared) return declared;
  // An enumerated value is a picker whether or not anyone listed it here, and the list comes
  // from the server's own `Spec.enum` so the two cannot drift.
  if (setting.options && setting.options.length > 0) return { kind: "options" };
  if (typeof setting.value === "boolean") return { kind: "switch" };
  if (typeof setting.value === "number") return { kind: "number" };
  // Reached only by a setting stored as a real JSON array rather than a comma-joined string;
  // the tags row branches on the same test to decide which form it sends back.
  if (Array.isArray(setting.value)) return { kind: "tags" };
  return { kind: "text" };
}

const UNIT_BYTES = { MiB: 1024 * 1024, GiB: 1024 * 1024 * 1024 } as const;

/** Bytes as a count of `unit`. `bytes` is what the server stores; the box shows this. */
export function toUnit(bytes: number, unit: "MiB" | "GiB"): number {
  return bytes / UNIT_BYTES[unit];
}

/** Back to whole bytes. Rounded: `min_free_bytes` is an int and the server refuses 1.5. */
export function toBytes(amount: number, unit: "MiB" | "GiB"): number {
  return Math.round(amount * UNIT_BYTES[unit]);
}

/** A comma-joined setting as pills. Trims, drops empties, so "" is []. */
export function splitList(text: string): string[] {
  return text
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/** Pills back to the stored form. */
export function joinList(tags: string[]): string {
  return tags.join(",");
}

/**
 * Every IANA zone the browser knows, with `current` appended if it is not among them.
 *
 * A platform call, not a bundled list: every browser this app supports and the node that runs
 * the tests already carry the zone database, and a copy in the bundle would be stale ICU. The
 * guard is for the build where the call is missing -- then the picker shows only what is set,
 * which is worse than a full list but better than a page that throws while rendering it. The
 * append is for the opposite case: a stored zone this build's ICU does not carry still displays
 * instead of the Select blanking and looking unset.
 */
export function timeZones(current: string): string[] {
  const zones =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];
  return zones.includes(current) ? zones : [...zones, current];
}

export function render(value: unknown): string {
  return typeof value === "string" ? value : JSON.stringify(value);
}

/**
 * Read a typed value back out of a text field.
 *
 * The current value decides how: a key holding a list expects JSON. Guessing from the text
 * alone would turn a string that happened to parse as a number into one -- which is also why
 * numbers never reach here: their field has parsed them already.
 */
export function parse(text: string, current: unknown): unknown {
  if (typeof current === "string") return text;
  try {
    return JSON.parse(text);
  } catch {
    // Send it as typed and let the server say what is wrong with it, rather than inventing
    // a client-side error message that may not match the server's rule.
    return text;
  }
}
